// Searchable branch picker. multiple: checkboxes, empty means every branch.
// single: one branch (a booking, number, or account belongs to one place).
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function branchText(branch) {
  return branch.label || branch.name || `Branch ${branch.franchise_id}`;
}

function placeMenu(button) {
  const rect = button.getBoundingClientRect();
  const width = Math.max(rect.width, 280);
  let left = rect.left;
  if (left + width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - 8 - width);
  const spaceBelow = window.innerHeight - rect.bottom;
  const openUp = spaceBelow < 240 && rect.top > spaceBelow;
  return {
    left,
    width,
    top: openUp ? undefined : rect.bottom + 4,
    bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
    maxHeight: Math.max(160, (openUp ? rect.top : spaceBelow) - 12),
  };
}

export default function BranchSelect({
  branches,
  value,
  onChange,
  multiple = false,
  placeholder = 'Choose the branch…',
  emptyLabel = '',
  disabled = false,
  optionMeta,
}) {
  const listId = useId();
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [box, setBox] = useState(null);

  const selectedIds = multiple ? (Array.isArray(value) ? value.map(String) : []) : [];
  const selectedOne = multiple ? '' : value == null ? '' : String(value);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (branches || [])
      .map((branch) => {
        const meta = optionMeta ? optionMeta(branch) || {} : {};
        return {
          branch,
          id: String(branch.franchise_id),
          label: `${branchText(branch)}${meta.suffix || ''}`,
          disabled: Boolean(meta.disabled),
        };
      })
      .filter((row) => !needle || row.label.toLowerCase().includes(needle));
  }, [branches, optionMeta, query]);

  const closedLabel = () => {
    if (multiple) {
      if (selectedIds.length === 0) return placeholder;
      if (selectedIds.length === 1) {
        const match = (branches || []).find((branch) => String(branch.franchise_id) === selectedIds[0]);
        return match ? branchText(match) : placeholder;
      }
      return `${selectedIds.length} branches`;
    }
    if (!selectedOne) return emptyLabel || placeholder;
    const match = (branches || []).find((branch) => String(branch.franchise_id) === selectedOne);
    return match ? branchText(match) : emptyLabel || placeholder;
  };

  useEffect(() => {
    if (!open) return undefined;
    const button = buttonRef.current;
    if (button) setBox(placeMenu(button));
    const onPlace = () => {
      if (buttonRef.current) setBox(placeMenu(buttonRef.current));
    };
    const onPointer = (event) => {
      const target = event.target;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener('resize', onPlace);
    window.addEventListener('scroll', onPlace, true);
    document.addEventListener('mousedown', onPointer);
    const focusTimer = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('resize', onPlace);
      window.removeEventListener('scroll', onPlace, true);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const onMenuKey = (event) => {
    if (event.key !== 'Escape' || !open) return;
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    close();
  };

  const toggle = (id) => {
    if (!multiple) {
      onChange(id);
      close();
      return;
    }
    const next = selectedIds.includes(id) ? selectedIds.filter((item) => item !== id) : [...selectedIds, id];
    onChange(next);
  };

  const selectVisible = () => {
    const ids = rows.filter((row) => !row.disabled).map((row) => row.id);
    onChange([...new Set([...selectedIds, ...ids])]);
  };

  const menu = open && box ? (
    <div
      ref={menuRef}
      className="de-branch-select__menu"
      style={{ left: box.left, width: box.width, top: box.top, bottom: box.bottom, maxHeight: box.maxHeight }}
      onKeyDown={onMenuKey}
    >
      <input
        ref={searchRef}
        className="de-input de-input--bordered de-branch-select__search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search branches"
        aria-label="Search branches"
      />
      {multiple ? (
        <div className="de-branch-select__actions">
          <button type="button" className="de-link-btn" onClick={selectVisible}>
            Select all
          </button>
          <button type="button" className="de-link-btn" onClick={() => onChange([])}>
            Clear
          </button>
        </div>
      ) : null}
      <div className="de-branch-select__list" role="listbox" id={listId} aria-multiselectable={multiple || undefined}>
        {!multiple && emptyLabel ? (
          <button type="button" className={`de-branch-select__option${selectedOne === '' ? ' is-selected' : ''}`} onClick={() => { onChange(''); close(); }}>
            {emptyLabel}
          </button>
        ) : null}
        {rows.length === 0 ? <div className="de-branch-select__empty">No branches match</div> : null}
        {rows.map((row) => {
          const checked = multiple ? selectedIds.includes(row.id) : selectedOne === row.id;
          if (multiple) {
            return (
              <label key={row.id} className={`de-branch-select__option${row.disabled ? ' is-disabled' : ''}${checked ? ' is-selected' : ''}`}>
                <input type="checkbox" checked={checked} disabled={row.disabled} onChange={() => toggle(row.id)} />
                <span>{row.label}</span>
              </label>
            );
          }
          return (
            <button
              key={row.id}
              type="button"
              className={`de-branch-select__option${row.disabled ? ' is-disabled' : ''}${checked ? ' is-selected' : ''}`}
              disabled={row.disabled}
              onClick={() => toggle(row.id)}
            >
              {row.label}
            </button>
          );
        })}
      </div>
      {multiple && selectedIds.length === 0 ? (
        <div className="de-branch-select__hint">Nothing checked shows every branch.</div>
      ) : null}
    </div>
  ) : null;

  return (
    <div className={`de-branch-select${disabled ? ' is-disabled' : ''}`} ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="de-branch-select__button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => {
          if (disabled) return;
          if (open) {
            close();
            return;
          }
          if (buttonRef.current) setBox(placeMenu(buttonRef.current));
          setOpen(true);
        }}
        onKeyDown={onMenuKey}
      >
        <span className="de-branch-select__value">{closedLabel()}</span>
        <span className="de-branch-select__chevron" aria-hidden="true">▾</span>
      </button>
      {menu ? createPortal(menu, document.body) : null}
    </div>
  );
}
